import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom";
import { addUser, deleteUser, updateUser, fetchUsers } from "../../redux/slices/adminSlice";

const UserManagement = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {user} = useSelector((state) => state.auth);
    const {users = [], loading, error} = useSelector((state) => state.admin || {});
    const [deletingUserId, setDeletingUserId] = useState(null);

    useEffect(() => {
        if(user && user.role !== "admin") {
            navigate("/");
        } else {
            // Fetch users when component mounts
            dispatch(fetchUsers());
        }
    }, [user, navigate, dispatch]);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        role: "customer",
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await dispatch(addUser(formData)).unwrap();
            // Reset The Form After Submission
            setFormData({
                name: "",
                email: "",
                password: "",
                role: "customer",
            });
        } catch (err) {
            console.error("Failed to add user:", err);
        }
    };

    const handleRoleChange = (userId, newRole) => {
        dispatch(updateUser({ id: userId, role: newRole }));
    };

    const handleDeleteUser = async (userId) => {
        if(window.confirm("Are you sure you want to delete this user?")) {
            try {
                setDeletingUserId(userId);
                await dispatch(deleteUser(userId)).unwrap();
                setDeletingUserId(null);
            } catch (err) {
                setDeletingUserId(null);
                console.error("Failed to delete user:", err);
            }
        }     
    };

    return (
        <div className="max-w-7xl mx-auto p-6">
            <h2 className="text-2xl font-bold mb-4">User Management</h2>
            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500">Error: {error}</p>}

            {/* Add new User Form */}
            <div className="p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold mb-4">Add New User</h3>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="text-gray-700">Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full p-2 border rounded" required />
                    </div>

                    <div className="mb-4">
                        <label className="text-gray-700">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-2 border rounded" required />
                    </div>

                    <div className="mb-4">
                        <label className="text-gray-700">Password</label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="w-full p-2 border rounded" required />
                    </div>

                    <div className="mb-4">
                        <label className="text-gray-700">Role</label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            className="w-full p-2 border rounded">
                            <option value="customer">Customer</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        className="w-full bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600">
                        Add User
                    </button>
                </form>
            </div>

            {/* User List Management */}
            <div className="overflow-x-auto shadow-md sm:rounded-lg">
                <table className="min-w-full text-left text-gray-500">
                    <thead className="bg-gray-100 text-xs uppercase text-gray-700">
                        <tr>
                            <th className="py-3 px-4">Name</th>
                            <th className="py-3 px-4">Email</th>
                            <th className="py-3 px-4">Role</th>
                            <th className="py-3 px-4">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {Array.isArray(users) && users.length > 0 ? (
                            users.map((user) => (
                                user && (
                                    <tr key={user._id || index} className="border hover:bg-gray-50">
                                        <td className="p-4 font-medium text-gray-900 whitespace-nowrap">
                                            {user.name || 'N/A'}
                                        </td>
                                        <td className="p-4">{user.email || 'N/A'}</td>
                                        <td className="p-4">
                                            <select
                                                value={user.role || 'customer'}
                                                onChange={(e) => handleRoleChange(user._id, e.target.value)}
                                                className="p-2 border rounded">
                                                <option value="customer">Customer</option>
                                                <option value="admin">Admin</option>
                                            </select>
                                        </td>
                                        <td className="p-4">
                                            <button
                                                onClick={() => handleDeleteUser(user._id)}
                                                disabled={deletingUserId === user._id}
                                                className={`${
                                                    deletingUserId === user._id 
                                                        ? "bg-gray-400" 
                                                        : "bg-red-500 hover:bg-red-600"
                                                } text-white py-2 px-4 rounded`}>
                                                {deletingUserId === user._id ? "Deleting..." : "Delete"}
                                            </button>
                                        </td>
                                    </tr>
                                )
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="p-4 text-center">
                                    {loading ? "Loading users..." : "No users found."}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default UserManagement;
